"use client";

import React from "react";
import { cn } from "@gettactic/helpers";
import { User } from "lucide-react";

export type VisitorLabelVariant = "light" | "dark";

const variantStyles: Record<
  VisitorLabelVariant,
  {
    name: string;
    subtitle: string;
    avatarPlaceholder: string;
    avatarIcon: string;
  }
> = {
  light: {
    name: "text-neutral-900",
    subtitle: "text-content",
    avatarPlaceholder: "bg-neutral-100",
    avatarIcon: "text-neutral-400"
  },
  dark: {
    name: "text-white",
    subtitle: "text-white/80",
    avatarPlaceholder: "bg-white/20",
    avatarIcon: "text-white/70"
  }
};

export interface VisitorLabelProps {
  /** Visitor's display name */
  name: string;
  /** Optional subtitle (e.g., check-in time, company) */
  subtitle?: string;
  /** URL to visitor's photo */
  photoUrl?: string | null;
  /** Visual variant */
  variant?: VisitorLabelVariant;
  /** Size of the avatar */
  size?: "sm" | "md" | "lg";
  /** Additional class name for the container */
  className?: string;
}

const sizeStyles = {
  sm: {
    avatar: "h-8 w-8",
    icon: "h-4 w-4",
    name: "text-sm",
    subtitle: "text-xs"
  },
  md: {
    avatar: "h-12 w-12",
    icon: "h-6 w-6",
    name: "text-base",
    subtitle: "text-sm"
  },
  lg: {
    avatar: "h-16 w-16",
    icon: "h-8 w-8",
    name: "text-lg",
    subtitle: "text-base"
  }
};

/** A visitor in the kiosk lists: their check-in photo (or an icon), name and a line about the visit. */
export const VisitorLabel: React.FC<VisitorLabelProps> = ({
  name,
  subtitle,
  photoUrl,
  variant = "light",
  size = "md",
  className
}) => {
  const styles = variantStyles[variant];
  const sizes = sizeStyles[size];

  return (
    // Spans only: the label sits inside the buttons of SearchableList
    <span className={cn("flex items-center gap-3", className)}>
      {/* Avatar */}
      {photoUrl ? (
        <img
          src={photoUrl}
          // The name is written next to the photo
          alt=""
          className={cn(
            "flex-shrink-0 rounded-full object-cover",
            sizes.avatar
          )}
        />
      ) : (
        <span
          className={cn(
            "flex flex-shrink-0 items-center justify-center rounded-full",
            styles.avatarPlaceholder,
            sizes.avatar
          )}
        >
          <User className={cn(styles.avatarIcon, sizes.icon)} />
        </span>
      )}

      {/* Info */}
      <span className="block min-w-0 flex-1">
        <span
          className={cn(
            "block truncate font-semibold",
            styles.name,
            sizes.name
          )}
        >
          {name}
        </span>
        {subtitle && (
          <span
            className={cn(
              "mt-0.5 block truncate",
              styles.subtitle,
              sizes.subtitle
            )}
          >
            {subtitle}
          </span>
        )}
      </span>
    </span>
  );
};

export interface VisitorLabelSkeletonProps {
  /** Visual variant */
  variant?: VisitorLabelVariant;
  /** Size of the avatar */
  size?: "sm" | "md" | "lg";
  /** Additional class name */
  className?: string;
}

/** The placeholder of a `VisitorLabel` while a list loads. */
export const VisitorLabelSkeleton: React.FC<VisitorLabelSkeletonProps> = ({
  variant = "light",
  size = "md",
  className
}) => {
  const sizes = sizeStyles[size];
  const bgColor = variant === "dark" ? "bg-white/10" : "bg-neutral-200";

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div
        className={cn(
          "flex-shrink-0 animate-pulse rounded-full",
          bgColor,
          sizes.avatar
        )}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className={cn("h-5 w-40 animate-pulse rounded", bgColor)} />
        <div className={cn("h-4 w-24 animate-pulse rounded", bgColor)} />
      </div>
    </div>
  );
};
